import { useState, useEffect, useCallback } from 'react';
import { X, BookOpen, Lightbulb, ChevronDown, ChevronUp, ExternalLink, Loader2, Sparkles } from 'lucide-react';

// 来源类型
type SourceType = 'zhangxuefeng' | 'mendao' | 'tips' | 'manual' | 'general';

interface KnowledgeItem {
  id: number;
  title: string;
  content: string;
  source: string;
  sourceType: SourceType;
  tags: string[];
  sections: Record<string, string> | null;
  relevanceScore: number;
  createdAt: string;
}

interface KnowledgeCategory {
  majorName: string;
  totalCount: number;
  categories: {
    '详细解读': KnowledgeItem[];
    '相关技巧': KnowledgeItem[];
  };
}

interface MajorKnowledgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  majorName: string;
}

// 来源标签配置
const SOURCE_CONFIG: Record<SourceType, { label: string; color: string; bgColor: string; icon: string }> = {
  zhangxuefeng: { label: '张雪峰', color: 'text-red-600', bgColor: 'bg-red-100', icon: '🔴' },
  mendao: { label: '门道课程', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: '🔵' },
  tips: { label: '志愿技巧', color: 'text-orange-600', bgColor: 'bg-orange-100', icon: '🟠' },
  manual: { label: '专业解读', color: 'text-green-600', bgColor: 'bg-green-100', icon: '🟢' },
  general: { label: '知识库', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: '⚪' },
};

export default function MajorKnowledgeModal({ isOpen, onClose, majorName }: MajorKnowledgeModalProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<KnowledgeCategory | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<'详细解读' | '相关技巧'>('详细解读');

  // 加载数据
  useEffect(() => {
    if (!isOpen || !majorName) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/knowledge/major/${encodeURIComponent(majorName)}/full`);
        const result = await res.json();
        if (result.success) {
          setData(result.data);
        } else {
          setData(null);
        }
      } catch (e) {
        console.error('加载专业知识失败:', e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, majorName]);

  // ESC键关闭
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  // 切换展开状态
  const toggleExpand = useCallback((id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // 获取来源配置
  const getSourceConfig = (item: KnowledgeItem) => {
    return SOURCE_CONFIG[item.sourceType] || SOURCE_CONFIG.general;
  };

  // 截断内容
  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.slice(0, maxLength) + '...';
  };

  // 渲染结构化内容
  const renderStructuredContent = (content: string, sections: Record<string, string> | null) => {
    // Markdown 渲染函数
    const renderMarkdown = (text: string) => {
      // 按行处理
      return text.split('\n').map((line, lineIndex) => {
        // 检测标题行（一、二、三... 或 第X章/节/条）
        if (/^[一二三四五六七八九十、]/.test(line) || /^第[一二三四五六七八九十]+[章节条点]/.test(line)) {
          return <p key={lineIndex} className="font-semibold text-gray-800 mt-3 mb-1 text-base">{renderInlineMarkdown(line)}</p>;
        }
        // 检测列表项（- 或 * 开头）
        if (/^[-*]\s/.test(line)) {
          return <p key={lineIndex} className="pl-4 mt-1"><span className="text-blue-500 mr-2">•</span>{renderInlineMarkdown(line.replace(/^[-*]\s/, ''))}</p>;
        }
        // 普通段落
        return <p key={lineIndex} className="mt-1">{renderInlineMarkdown(line)}</p>;
      });
    };

    // 行内 Markdown 渲染（加粗、斜体）
    const renderInlineMarkdown = (text: string) => {
      const parts: React.ReactNode[] = [];
      let remaining = text;
      let keyIndex = 0;

      while (remaining.length > 0) {
        // 匹配加粗 **text** 或 __text__
        const boldMatch = remaining.match(/^([^*_]*?)\*\*(.+?)\*\*([^*]*)$/) 
          || remaining.match(/^([^_*]*?)__(.+?)__([^_*]*)$/);
        if (boldMatch) {
          if (boldMatch[1]) parts.push(<span key={keyIndex++}>{boldMatch[1]}</span>);
          parts.push(<strong key={keyIndex++} className="font-bold text-gray-900">{boldMatch[2]}</strong>);
          remaining = boldMatch[3] || '';
          continue;
        }

        // 匹配斜体 *text* 或 _text_
        const italicMatch = remaining.match(/^([^*_]*?)\*(.+?)\*([^*]*)$/)
          || remaining.match(/^([^_*]*?)_(.+?)_([^_*]*)$/);
        if (italicMatch) {
          if (italicMatch[1]) parts.push(<span key={keyIndex++}>{italicMatch[1]}</span>);
          parts.push(<em key={keyIndex++} className="italic text-gray-700">{italicMatch[2]}</em>);
          remaining = italicMatch[3] || '';
          continue;
        }

        // 没有匹配到任何格式，直接返回剩余文本
        parts.push(<span key={keyIndex++}>{remaining}</span>);
        break;
      }

      return parts;
    };

    if (!sections || Object.keys(sections).length === 0) {
      return (
        <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
          {renderMarkdown(content)}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {Object.entries(sections).map(([title, text]) => (
          <div key={title} className="border-b border-gray-100 pb-2 last:border-0">
            <h5 className="text-sm font-semibold text-blue-600 mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              {title}
            </h5>
            <div className="text-sm text-gray-600 leading-relaxed pl-3.5">
              {renderMarkdown(text)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 渲染知识卡片
  const renderKnowledgeCard = (item: KnowledgeItem, isLongContent: boolean) => {
    const isExpanded = expandedIds.has(item.id);
    const sourceConfig = getSourceConfig(item);
    const shortContent = truncateContent(item.content, 150);
    const showExpand = isLongContent && item.content.length > 150;

    return (
      <div key={item.id} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
        {/* 卡片头部 */}
        <div
          className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white cursor-pointer hover:from-blue-50 hover:to-white transition-colors"
          onClick={() => toggleExpand(item.id)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${sourceConfig.bgColor} ${sourceConfig.color}`}>
                {sourceConfig.icon} {sourceConfig.label}
              </span>
              <h4 className="font-medium text-gray-800 truncate text-sm">{item.title}</h4>
            </div>
            {showExpand && (
              <span className="text-gray-400 shrink-0">
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </span>
            )}
          </div>
        </div>

        {/* 卡片内容 */}
        <div className="px-4 py-3 border-t border-gray-100">
          {!isExpanded ? (
            <p className="text-sm text-gray-600 leading-relaxed">{shortContent}</p>
          ) : (
            renderStructuredContent(item.content, item.sections)
          )}
          
          {showExpand && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleExpand(item.id); }}
              className="mt-2 text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1 font-medium"
            >
              <ExternalLink size={12} />
              {isExpanded ? '收起' : '展开全文'}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const details = data?.categories?.['详细解读'] || [];
  const tips = data?.categories?.['相关技巧'] || [];
  const currentItems = activeTab === '详细解读' ? details : tips;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-gradient-to-br from-slate-50 to-white rounded-3xl shadow-2xl w-full max-w-[95vw] md:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* 顶部标题栏 */}
        <div className="px-6 py-4 border-b bg-white/80 backdrop-blur-sm flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">{majorName}</h2>
              <p className="text-xs text-gray-500">专业详解</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* 加载状态 */}
        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 size={32} className="animate-spin text-blue-500 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">正在加载专业知识...</p>
            </div>
          </div>
        )}

        {/* 空状态 */}
        {!loading && !data && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center py-12">
              <BookOpen size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">暂无该专业的详细解读</p>
              <p className="text-gray-400 text-sm mt-1">知识库持续更新中</p>
            </div>
          </div>
        )}

        {/* 有数据时的内容 */}
        {!loading && data && (
          <>
            {/* Tab切换 */}
            <div className="px-6 py-3 border-b bg-gray-50/50 flex items-center gap-4 shrink-0">
              <button
                onClick={() => setActiveTab('详细解读')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === '详细解读'
                    ? 'bg-white text-blue-600 shadow-sm border border-blue-100'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                }`}
              >
                <BookOpen size={16} />
                详细解读
                {details.length > 0 && (
                  <span className="bg-blue-100 text-blue-600 text-xs px-1.5 py-0.5 rounded-full">
                    {details.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('相关技巧')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === '相关技巧'
                    ? 'bg-white text-orange-600 shadow-sm border border-orange-100'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                }`}
              >
                <Lightbulb size={16} />
                相关技巧
                {tips.length > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-xs px-1.5 py-0.5 rounded-full">
                    {tips.length}
                  </span>
                )}
              </button>
            </div>

            {/* 内容区域 */}
            <div className="flex-1 overflow-y-auto p-6">
              {currentItems.length === 0 ? (
                <div className="py-12 text-center">
                  <Lightbulb size={32} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    {activeTab === '详细解读' ? '暂无详细解读内容' : '暂无相关技巧'}
                  </p>
                </div>
              ) : (
                <div className={`grid gap-4 ${
                  activeTab === '详细解读' 
                    ? 'grid-cols-1 lg:grid-cols-2' 
                    : 'grid-cols-1'
                }`}>
                  {currentItems.map(item => renderKnowledgeCard(item, activeTab === '详细解读'))}
                </div>
              )}
            </div>

            {/* 底部说明 */}
            <div className="px-6 py-3 border-t bg-gray-50/50 flex items-center justify-between text-xs text-gray-400 shrink-0">
              <span>共 {data.totalCount} 条相关内容</span>
              <span>知识来源：张雪峰 · 门道系列 · 专业解读手册</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
